import { CsvTemplateLink } from '../../components/CsvTemplateLink';
import type { ValidationIssue } from '@royaltyflow/validation';
import { ValidationIssuePanel, validationIssues } from '../../components/ValidationIssuePanel';
import { forwardRef, useState } from 'react';

import { useSalesImport, useShows } from '../../api/hooks';
import { Button, FileDropzone, FormField, Select, useToast } from '../../components/DesignSystem';
import { CSV_COLUMNS, CSV_LIMIT, inspectCsvHeader, issue } from '../../lib/csvValidation';
import { errorMessage } from '../../lib/errors';
import { readFileText } from '../../lib/files';

interface SecondaryDataCardProps {
  /** Preselected show id (set by the table's "+" action). */
  preselectedShowId: string | null;
}

/**
 * "Add Secondary report data" — routes one sales CSV to multiple seasons
 * by season_id and creates draft simulations for affected contracts.
 */
export const SecondaryDataCard = forwardRef<HTMLElement, SecondaryDataCardProps>(
  function SecondaryDataCard({ preselectedShowId }, ref) {
    const { showToast } = useToast();
    const shows = useShows({ limit: 100 });
    const salesImport = useSalesImport();

    const [replacePeriods, setReplacePeriods] = useState(false);
    const [showChoice, setShowChoice] = useState('');
    const [seasonIds, setSeasonIds] = useState<string[]>([]);
    const [csvFile, setCsvFile] = useState<File | null>(null);
    const [issues, setIssues] = useState<ValidationIssue[]>([]);
    const [isImporting, setIsImporting] = useState(false);

    const effectiveShowId = showChoice === '' ? (preselectedShowId ?? '') : showChoice;
    const selectedShow = shows.data?.data.find((show) => show.id === effectiveShowId);

    function toggleSeason(seasonId: string) {
      setSeasonIds((current) =>
        current.includes(seasonId)
          ? current.filter((id) => id !== seasonId)
          : [...current, seasonId],
      );
    }

    async function handleAdd() {
      if (isImporting) return;
      const missing: ValidationIssue[] = [];
      if (effectiveShowId === '')
        missing.push(issue('required_field', 'Select a show', 'sales', 'showId'));
      if (seasonIds.length === 0)
        missing.push(issue('required_field', 'Select at least one season', 'sales', 'seasonIds'));
      if (csvFile === null)
        missing.push(issue('required_field', 'Choose a CSV file', 'sales', 'file'));
      if (missing.length || csvFile === null) {
        setIssues(missing);
        return;
      }

      setIsImporting(true);

      try {
        if (csvFile.size > CSV_LIMIT) {
          setIssues([issue('csv_limit_exceeded', 'CSV exceeds the 2 MB file limit', 'sales', '')]);
          return;
        }
        const csvText = await readFileText(csvFile);
        const header = inspectCsvHeader(csvText, 'sales');
        if (header.issues.length) {
          setIssues(header.issues);
          return;
        }
        const result = await salesImport.mutateAsync({
          mode: replacePeriods ? 'replace_periods' : 'append',
          showId: effectiveShowId,
          seasonIds,
          csvText,
          fileName: csvFile.name,
        });
        showToast(
          `${result.rowsImported} rows imported — ${result.draftSimulations.length} draft simulation(s) created`,
        );
        setIssues([]);
        setCsvFile(null);
        setReplacePeriods(false);
        setSeasonIds([]);
      } catch (error) {
        setIssues(validationIssues(error));
        showToast(errorMessage(error, 'Sales import failed'), 'error');
      } finally {
        setIsImporting(false);
      }
    }

    return (
      <section className="rfp-card" ref={ref}>
        <div className="rfp-card__head">
          <div>
            <h2 className="rfp-card__title">Add Secondary report data</h2>
            <p className="rfp-card__caption">
              Select a show, then choose one or more seasons. Upload a single sales CSV — rows will
              be routed to each season by season_id.
            </p>
          </div>
        </div>

        <div className="rfp-stack">
          <FormField htmlFor="secondary-show" label="Show">
            <Select
              id="secondary-show"
              onChange={(event) => {
                setShowChoice(event.target.value);
                setSeasonIds([]);
              }}
              value={effectiveShowId}
            >
              <option value="">Select a show…</option>
              {(shows.data?.data ?? []).map((show) => (
                <option key={show.id} value={show.id}>
                  {show.name}
                </option>
              ))}
            </Select>
          </FormField>

          {selectedShow && (
            <FormField label="Seasons">
              {selectedShow.seasons.length === 0 ? (
                <p className="rfp-muted-text">This show has no seasons yet.</p>
              ) : (
                <div className="rfp-check-grid">
                  {selectedShow.seasons.map((season) => (
                    <label className="rfp-check" key={season.id}>
                      <input
                        checked={seasonIds.includes(season.id)}
                        onChange={() => toggleSeason(season.id)}
                        type="checkbox"
                      />
                      {season.seasonCode}
                      {season.name ? ` — ${season.name}` : ''}
                    </label>
                  ))}
                </div>
              )}
            </FormField>
          )}

          <CsvTemplateLink entity="sales" />
          <label className="rfp-check">
            <input
              type="checkbox"
              checked={replacePeriods}
              disabled={isImporting}
              onChange={(event) => setReplacePeriods(event.target.checked)}
            />
            Replace previous CSV sales for matching seasons and exact reporting dates
          </label>
          <p className="rfp-card__caption">
            When selected, this file replaces all CSV sales with the same season, start date and end
            date as its rows. Include every sale for those periods. Manual entries and other periods
            are kept. Then rerun your simulation to refresh its results.
          </p>
          <FileDropzone
            accept=".csv,text/csv"
            hint={`CSV columns: ${[...CSV_COLUMNS.sales.required, ...CSV_COLUMNS.sales.optional].join(', ')}`}
            label={csvFile ? csvFile.name : 'Drop a sales CSV here or click to browse'}
            onFile={(file) => {
              if (!isImporting) {
                setCsvFile(file);
                setIssues([]);
              }
            }}
          />

          <div className="rfp-wizard-actions">
            <Button disabled={isImporting} onClick={handleAdd} type="button">
              {isImporting ? 'Importing…' : 'Add'}
            </Button>
          </div>
        </div>

        <ValidationIssuePanel issues={issues} />
      </section>
    );
  },
);
